import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { SchoolDocument, SchoolAssociation } from '../types';
import { getSchool } from '../services/dataService';
import { isPlatformOwner } from '../services/subscription';

export type SchoolContextType = 'personal' | SchoolAssociation;

interface SchoolContextValue {
  /** null = personal workspace; SchoolDocument when in a school context */
  currentSchool: SchoolDocument | null;
  /** The raw association record for the current school (null in personal workspace) */
  currentAssociation: SchoolAssociation | null;
  /** All school associations for the logged-in user */
  schoolAssociations: SchoolAssociation[];
  /** Switch context: 'personal' or a schoolId */
  switchContext: (to: 'personal' | string) => void;
  /** Replace currentSchool in-place (e.g. after editing branding/settings) so
   *  every consumer sees the new value without needing a full refetch. */
  updateCurrentSchool: (school: SchoolDocument) => void;
  loading: boolean;
  /** True if user has principal, vp, or office_staff role in current school */
  isSchoolAdmin: boolean;
  /** True when the PLATFORM OWNER is viewing a school they have no association
   *  with. `currentAssociation` is synthesized as principal in that case, so
   *  anything user-facing must surface this rather than pretending the owner
   *  really holds that role. */
  viewingAsOwner: boolean;
}

const SchoolCtx = createContext<SchoolContextValue | null>(null);

export const useSchool = (): SchoolContextValue => {
  const ctx = useContext(SchoolCtx);
  if (!ctx) throw new Error('useSchool must be used inside SchoolProvider');
  return ctx;
};

interface Props { children: ReactNode }

export const SchoolProvider: React.FC<Props> = ({ children }) => {
  const { userProfile } = useAuth();
  const associations: SchoolAssociation[] = userProfile?.schoolAssociations ?? [];

  // Per-uid storage key so different users on the same device don't see
  // each other's last-used context. Null until userProfile loads.
  const STORAGE_KEY = userProfile?.uid ? `luc_school_ctx_${userProfile.uid}` : null;

  // Sentinel stored under STORAGE_KEY when the user DELIBERATELY chose the
  // personal workspace (vs. "no context yet"). Without this, the auto-select
  // effect below can't tell the two apart and immediately re-applies the user's
  // first school — making the "Personal Workspace" switch a no-op for any
  // school-affiliated (non board-admin) user.
  const PERSONAL = 'personal';

  const [activeSchoolId, setActiveSchoolId] = useState<string | null>(null);
  const [currentSchool, setCurrentSchool] = useState<SchoolDocument | null>(null);
  const [loading, setLoading] = useState(false);
  // Set once userProfile resolves and we've attempted hydration. Until then,
  // the verification effect should NOT clear activeSchoolId — that was the
  // bug where refresh inside a school kicked you back to the board view.
  const [hydrated, setHydrated] = useState(false);

  // Hydrate activeSchoolId from localStorage as soon as we know the uid.
  useEffect(() => {
    if (!STORAGE_KEY) return;
    const saved = localStorage.getItem(STORAGE_KEY);
    // A stored PERSONAL sentinel means "stay in personal workspace" — leave
    // activeSchoolId null. Any other value is a real schoolId to restore.
    if (saved && saved !== PERSONAL) setActiveSchoolId(saved);
    setHydrated(true);
  }, [STORAGE_KEY]);

  // Auto-select the user's first school association when there's no
  // saved context yet AND they aren't a board admin. Board admins
  // intentionally land at the board level; school-affiliated users
  // (principals, VPs, teachers) should drop straight into their
  // school. Specifically fixes brand-new invitees whose localStorage
  // doesn't have a context key — without this they'd land on the
  // board-level shell with no school selected and an almost-empty
  // sidebar.
  useEffect(() => {
    if (!hydrated) return;
    if (activeSchoolId) return;
    if (!userProfile?.uid) return;
    if (userProfile.role === 'board_admin') return;
    if (associations.length === 0) return;
    // Respect a deliberate "Personal Workspace" choice — don't snap them back
    // into their school.
    if (STORAGE_KEY && localStorage.getItem(STORAGE_KEY) === PERSONAL) return;
    const firstSchoolId = associations[0].schoolId;
    setActiveSchoolId(firstSchoolId);
    if (STORAGE_KEY) localStorage.setItem(STORAGE_KEY, firstSchoolId);
  }, [hydrated, activeSchoolId, userProfile?.uid, userProfile?.role, associations.length, STORAGE_KEY]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inject / remove school branding CSS custom properties on context change
  useEffect(() => {
    const root = document.documentElement;
    if (currentSchool?.branding) {
      root.style.setProperty('--school-primary', currentSchool.branding.primaryColor);
      root.style.setProperty('--school-secondary', currentSchool.branding.secondaryColor);
      root.style.setProperty('--school-accent', currentSchool.branding.accentColor);
      if (currentSchool.branding.logoUrl) {
        root.style.setProperty('--school-logo', `url(${currentSchool.branding.logoUrl})`);
      } else {
        root.style.removeProperty('--school-logo');
      }
    } else {
      root.style.removeProperty('--school-primary');
      root.style.removeProperty('--school-secondary');
      root.style.removeProperty('--school-accent');
      root.style.removeProperty('--school-logo');
    }
  }, [currentSchool]);

  // When active school changes, load it from Firestore. Two important
  // guards: don't run before hydration finishes, and don't clear
  // activeSchoolId until we actually know whether the user has the
  // association — userProfile.schoolAssociations populates async via the
  // Firestore listener, so an empty array on first render doesn't mean
  // "no membership", it means "still loading".
  useEffect(() => {
    if (!hydrated) return;
    if (!activeSchoolId) {
      setCurrentSchool(null);
      return;
    }
    // Wait for userProfile to actually resolve before deciding membership.
    if (!userProfile?.uid) return;
    const assoc = associations.find(a => a.schoolId === activeSchoolId);
    // PLATFORM OWNER may enter a school they have no association with.
    //
    // This is what made "view as school admin" useless: the owner is a
    // board_admin with no schoolAssociation for (say) OEO's school, so this
    // branch fired the moment they picked it — wiping activeSchoolId and
    // bouncing them straight back to the board view. Flipping render gates in
    // AdminDashboard could never fix that, because every school-scoped panel
    // reads `currentSchool` from here.
    //
    // Gated on isPlatformOwner (email allow-list), NOT on role === 'board_admin'.
    // A customer holding board_admin must never silently gain entry to schools
    // outside their own board, and role is self-assertable in a way the
    // allow-list is not.
    if (!assoc && !isPlatformOwner(userProfile)) {
      // Genuinely not a member of this school anymore (e.g. removed). Clean
      // up localStorage too so the next reload doesn't keep retrying.
      if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
      setActiveSchoolId(null);
      setCurrentSchool(null);
      return;
    }
    setLoading(true);
    getSchool(activeSchoolId)
      .then(school => setCurrentSchool(school))
      .catch(() => setCurrentSchool(null))
      .finally(() => setLoading(false));
    // include associations.length so the effect re-runs when the live
    // listener populates the user's school list after a refresh
  }, [activeSchoolId, userProfile?.uid, associations.length, hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchContext = (to: 'personal' | string) => {
    if (to === 'personal') {
      setActiveSchoolId(null);
      setCurrentSchool(null);
      // Persist the sentinel (not removeItem) so the choice survives reloads and
      // the auto-select effect doesn't bounce a school-affiliated user back.
      if (STORAGE_KEY) localStorage.setItem(STORAGE_KEY, PERSONAL);
    } else {
      // Push a history entry tagged for this school context so two-finger
      // swipe / browser Back returns the user to the board view instead of
      // navigating away from the app entirely.
      try {
        window.history.pushState({ schoolContext: to }, '', window.location.href);
      } catch {
        // ignore — non-fatal, just means browser-back won't pop us out
      }
      setActiveSchoolId(to);
      // "View as school admin" is deliberately NOT sticky.
      //
      // Persisting a school the user has no association with meant a reload
      // silently dropped the owner back inside a customer's school — and since
      // the banner and the owner-nav gating are derived per-render, a restored
      // context that nobody deliberately re-entered is an invitation to forget
      // where you are. Entering must be an explicit act every time, so we clear
      // the key rather than writing it: a reload lands back at the board view.
      const isForeign = !associations.some(a => a.schoolId === to);
      if (STORAGE_KEY) {
        if (isForeign) localStorage.removeItem(STORAGE_KEY);
        else localStorage.setItem(STORAGE_KEY, to);
      }
    }
  };

  // When a board admin hits Back (popstate) while inside a school
  // context, return them to the board view. Only board admins —
  // school-affiliated users (principal/vp/office_staff/teacher) have
  // no board view to return to, and the auto-select effect above
  // would immediately re-apply their school context anyway, causing
  // a visible flash. Plus, the URL gets cleaned (drop tab/draft/ptab)
  // so a board admin doesn't land on `?tab=builder` with no school
  // selected — an otherwise-unreachable state.
  useEffect(() => {
    if (!activeSchoolId) return;
    if (userProfile?.role !== 'board_admin') return;
    const handler = () => {
      setActiveSchoolId(null);
      setCurrentSchool(null);
      if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
      if (typeof window !== 'undefined') {
        const sp = new URLSearchParams(window.location.search);
        let changed = false;
        for (const key of ['tab', 'draft', 'ptab']) {
          if (sp.has(key)) { sp.delete(key); changed = true; }
        }
        if (changed) {
          const newUrl = window.location.pathname + (sp.toString() ? '?' + sp.toString() : '');
          window.history.replaceState({}, '', newUrl);
        }
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [activeSchoolId, userProfile?.role, STORAGE_KEY]);

  const realAssociation = activeSchoolId
    ? associations.find(a => a.schoolId === activeSchoolId) ?? null
    : null;

  /**
   * True when the platform owner is inside a school they don't belong to.
   *
   * Kept as its own flag rather than being folded invisibly into
   * currentAssociation, because the UI must SAY so. Reece's requirement was
   * "owner only and explicit as to what it does" — an owner silently rendered as
   * a principal has no way to tell whether what they're looking at is really
   * what that principal sees.
   */
  const viewingAsOwner = !!activeSchoolId && !realAssociation && isPlatformOwner(userProfile);

  /**
   * Stand in as a principal for the school being viewed.
   *
   * Every school-scoped panel derives its permissions from currentAssociation —
   * `hasPermission(ctx, ...)` in services/permissions.ts reads its role and
   * delegated permissions. Leaving it null renders the shell with no school
   * admin rights at all, which is the "kind of useless" view. Synthesising a
   * principal is what makes the answer to "what do they see, and what options do
   * they have" actually correct: principal is the ceiling of school-level
   * permission, so nothing an admin can reach is hidden from this view.
   *
   * This changes CLIENT rendering only. Firestore rules remain the real
   * boundary — a synthesized association grants no read or write the owner's own
   * credentials didn't already carry.
   */
  const currentAssociation: SchoolAssociation | null = realAssociation
    ?? (viewingAsOwner && activeSchoolId
      ? {
          schoolId: activeSchoolId,
          boardId: currentSchool?.boardId ?? '',
          role: 'principal',
          context: currentSchool?.name ?? 'School',
        }
      : null);

  const updateCurrentSchool = (school: SchoolDocument) => {
    setCurrentSchool(school);
  };

  return (
    <SchoolCtx.Provider
      value={{
        currentSchool,
        currentAssociation,
        schoolAssociations: associations,
        switchContext,
        updateCurrentSchool,
        loading,
        viewingAsOwner,
        isSchoolAdmin: currentAssociation
          ? ['principal', 'vp', 'office_staff'].includes(currentAssociation.role)
          : false,
      }}
    >
      {children}
    </SchoolCtx.Provider>
  );
};
